import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import MikaCard from '../../components/MikaCard/MikaCard';

interface WarehouseCardGridProps {
    data: any[];
    tab: 'pendingReceipt' | 'inWarehouse' | 'shipped' | 'purchasedMaterials' | 'innerStock';
    onRowClick?: (row: any) => void;
    onAccept?: (row: any) => void;
    onReject?: (row: any) => void;
    onEditArticle?: (row: any) => void;
}

const STATUS_VISUAL: Record<string, { color: string; bg: string; id: string; defaultMessage: string }> = {
    pending_receipt:   { color: '#f59e0b', bg: '#fef3c7', id: 'warehouse.status.pending',     defaultMessage: 'Për pranim' },
    in_warehouse:      { color: '#3b82f6', bg: '#dbeafe', id: 'warehouse.status.inWarehouse', defaultMessage: 'Në depo' },
    shipped:           { color: '#22c55e', bg: '#dcfce7', id: 'warehouse.status.shipped',     defaultMessage: 'E dërguar' },
    partially_shipped: { color: '#8b5cf6', bg: '#f3e8ff', id: 'warehouse.status.partial',     defaultMessage: 'Pjesërisht' },
};

const getInitials = (name: string): string => {
    return name.split(' ').map(w => w.charAt(0)).join('').substring(0, 2).toUpperCase() || '?';
};

const getAvatarColor = (name: string): string => {
    const colors = ['#3b82f6', '#8b5cf6', '#06b6d4', '#f59e0b', '#ef4444', '#22c55e', '#ec4899', '#6366f1'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
};

const getWaitTimeColor = (hours: number | null): string => {
    if (hours === null) return '#94a3b8';
    if (hours < 24) return '#22c55e';
    if (hours < 72) return '#f59e0b';
    return '#ef4444';
};

const WarehouseCardGrid: React.FC<WarehouseCardGridProps> = ({ data, tab, onRowClick, onAccept, onReject, onEditArticle }) => {
    const intl = useIntl();
    const fm = (id: string, defaultMessage: string, values?: Record<string, any>) =>
        intl.formatMessage({ id, defaultMessage }, values);

    const formatWaitTime = (hours: number | null): string => {
        if (hours === null || hours === undefined) return '\u2014';
        if (hours < 24) return fm('warehouse.time.hours', '{hours, plural, one {# orë} other {# orë}}', { hours });
        const days = Math.floor(hours / 24);
        return fm('warehouse.time.days', '{days, plural, one {# ditë} other {# ditë}}', { days });
    };

    if (data.length === 0) {
        return (
            <div className="ccg-grid">
                <div className="ccg-empty">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
                        <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
                    </svg>
                    <p><FormattedMessage id="common.noData" defaultMessage="Nuk ka të dhëna" /></p>
                </div>
            </div>
        );
    }

    // ── Pending receipt cards ──
    if (tab === 'pendingReceipt') {
        return (
            <div className="ccg-grid">
                {data.map((row) => {
                    const clientName = [row.clientName, row.clientLastName].filter(Boolean).join(' ');
                    const initials = getInitials(clientName);
                    return (
                        <MikaCard key={row.id}
                            header={
                                <div className="ccg-card-header">
                                    <div className="ccg-avatar" style={{ background: getAvatarColor(clientName) }}>{initials}</div>
                                    <div className="ccg-card-name">
                                        <span className="ccg-name">{row.articleName || '\u2014'}</span>
                                        {row.typeCode && <span className="ccg-company">{row.typeCode}</span>}
                                    </div>
                                    <span className="ccg-status-badge" style={{ color: '#f59e0b', background: '#fef3c7' }}>
                                        <FormattedMessage id="warehouse.status.pending" defaultMessage="Për pranim" />
                                    </span>
                                </div>
                            }
                            body={
                                <div className="ccg-card-body-content">
                                    <div className="ccg-info-grid">
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="production.detail.client" defaultMessage="Klienti" /></span>
                                            <span className="ccg-info-value">{clientName || '\u2014'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="production.detail.project" defaultMessage="Projekti" /></span>
                                            <span className="ccg-info-value">{row.projectName || '\u2014'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="production.detail.quantity" defaultMessage="Sasia" /></span>
                                            <span className="ccg-info-value" style={{ fontWeight: 600 }}>{row.pendingQuantity} / {row.quantity}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="production.responsibilityArchive.card.department" defaultMessage="Reparti" /></span>
                                            <span className="ccg-info-value">{row.departmentName || '\u2014'}</span>
                                        </div>
                                        <div className="ccg-info-item ccg-info-full">
                                            <span className="ccg-info-label"><FormattedMessage id="warehouse.col.waitTimePending" defaultMessage="Koha në pritje" /></span>
                                            <span className="ccg-info-value" style={{ color: getWaitTimeColor(row.pendingSinceHours) }}>{formatWaitTime(row.pendingSinceHours)}</span>
                                        </div>
                                    </div>
                                </div>
                            }
                            footer={
                                <div className="ccg-card-footer">
                                    <span className="ccg-date">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                                        {row.warehousePendingAt || '\u2014'}
                                    </span>
                                    <div className="ccg-footer-actions">
                                        {onAccept && <button className="ccg-action-btn" style={{ color: '#15803d' }} onClick={(e) => { e.stopPropagation(); onAccept(row); }} title={fm('warehouse.action.accept', 'Prano')}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                                        </button>}
                                        {onReject && <button className="ccg-action-btn ccg-action-delete" onClick={(e) => { e.stopPropagation(); onReject(row); }} title={fm('warehouse.action.reject', 'Kthe')}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                                        </button>}
                                    </div>
                                </div>
                            }
                        />
                    );
                })}
            </div>
        );
    }

    // ── Inventory cards (inWarehouse / shipped) ──
    if (tab === 'inWarehouse' || tab === 'shipped') {
        return (
            <div className="ccg-grid">
                {data.map((row) => {
                    const clientName = row.clientName || '';
                    const initials = getInitials(clientName);
                    const status = STATUS_VISUAL[row.warehouseStatus] || STATUS_VISUAL['in_warehouse'];
                    return (
                        <MikaCard key={row.id} onClick={onRowClick ? () => onRowClick(row) : undefined}
                            header={
                                <div className="ccg-card-header">
                                    <div className="ccg-avatar" style={{ background: getAvatarColor(clientName) }}>{initials}</div>
                                    <div className="ccg-card-name">
                                        <span className="ccg-name">{row.articleName || '\u2014'}</span>
                                        {row.typeCode && <span className="ccg-company">{row.typeCode}</span>}
                                    </div>
                                    <span className="ccg-status-badge" style={{ color: status.color, background: status.bg }}>{fm(status.id, status.defaultMessage)}</span>
                                </div>
                            }
                            body={
                                <div className="ccg-card-body-content">
                                    <div className="ccg-info-grid">
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="production.detail.client" defaultMessage="Klienti" /></span>
                                            <span className="ccg-info-value">{clientName || '\u2014'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="production.detail.project" defaultMessage="Projekti" /></span>
                                            <span className="ccg-info-value">{row.projectName || '\u2014'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="production.detail.quantity" defaultMessage="Sasia" /></span>
                                            <span className="ccg-info-value" style={{ fontWeight: 600 }}>{row.quantity ?? '\u2014'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="warehouse.col.waitTimeStorage" defaultMessage="Koha në depo" /></span>
                                            <span className="ccg-info-value" style={{ color: getWaitTimeColor(row.waitTimeHours) }}>{formatWaitTime(row.waitTimeHours)}</span>
                                        </div>
                                        {tab === 'inWarehouse' && row.receivedBy && (
                                            <div className="ccg-info-item ccg-info-full">
                                                <span className="ccg-info-label"><FormattedMessage id="warehouse.col.receivedBy" defaultMessage="Pranuar nga" /></span>
                                                <span className="ccg-info-value">{row.receivedBy}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            }
                            footer={
                                <div className="ccg-card-footer">
                                    <span className="ccg-date">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                                        {(tab === 'inWarehouse' ? row.receivedAt : row.shippedAt) || '\u2014'}
                                    </span>
                                    {tab === 'inWarehouse' && onRowClick && (
                                        <div className="ccg-footer-actions">
                                            <button className="ccg-action-btn" onClick={(e) => { e.stopPropagation(); onRowClick(row); }} title={fm('warehouse.action.createShipment', 'Krijo dërgesë')}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="3" width="15" height="13" /><polygon points="16 8 20 8 23 11 23 16 16 16 16 8" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></svg>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            }
                        />
                    );
                })}
            </div>
        );
    }

    // ── Purchased articles cards ──
    if (tab === 'purchasedMaterials') {
        return (
            <div className="ccg-grid">
                {data.map((row) => {
                    const name = row.articleName || row.articles?.[0]?.label || '\u2014';
                    return (
                        <MikaCard key={row.value}
                            header={
                                <div className="ccg-card-header">
                                    <div className="ccg-avatar" style={{ background: '#1e3a5f' }}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" /></svg>
                                    </div>
                                    <div className="ccg-card-name">
                                        <span className="ccg-name">{name}</span>
                                        {row.productCode && <span className="ccg-company">{row.productCode}</span>}
                                    </div>
                                </div>
                            }
                            body={
                                <div className="ccg-card-body-content">
                                    <div className="ccg-info-grid">
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="production.detail.quantity" defaultMessage="Sasia" /></span>
                                            <span className="ccg-info-value" style={{ fontWeight: 700, color: '#1e40af', fontSize: 14 }}>{row.quantity ?? '\u2014'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="warehouse.col.unit" defaultMessage="Njësia" /></span>
                                            <span className="ccg-info-value">{row.unitName || '\u2014'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="production.responsibilityArchive.card.department" defaultMessage="Reparti" /></span>
                                            <span className="ccg-info-value">{row.departmentName || '\u2014'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="warehouse.col.pricePerUnit" defaultMessage="Çmimi/njësi" /></span>
                                            <span className="ccg-info-value">{row.pricePerUnit ? `${Number(row.pricePerUnit).toFixed(2)}\u20AC` : '\u2014'}</span>
                                        </div>
                                        {row.totalValue && (
                                            <div className="ccg-info-item ccg-info-full">
                                                <span className="ccg-info-label"><FormattedMessage id="warehouse.col.totalValue" defaultMessage="Vlera totale" /></span>
                                                <span className="ccg-info-value" style={{ fontWeight: 600, color: '#1e40af' }}>{Number(row.totalValue).toFixed(2)}{'\u20AC'}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            }
                            footer={
                                <div className="ccg-card-footer">
                                    <span className="ccg-date">{row.updatedAt || '\u2014'}</span>
                                    {onEditArticle && (
                                        <div className="ccg-footer-actions">
                                            <button className="ccg-action-btn" onClick={(e) => { e.stopPropagation(); onEditArticle(row); }} title={fm('warehouse.action.editQuantity', 'Ndrysho sasinë')}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            }
                        />
                    );
                })}
            </div>
        );
    }

    // ── Inner stock cards ──
    if (tab === 'innerStock') {
        return (
            <div className="ccg-grid">
                {data.map((row, idx) => {
                    const clientName = row.clientName || '';
                    const initials = getInitials(clientName || '?');
                    return (
                        <MikaCard key={row.designation?.value || idx}
                            header={
                                <div className="ccg-card-header">
                                    <div className="ccg-avatar" style={{ background: getAvatarColor(clientName) }}>{initials}</div>
                                    <div className="ccg-card-name">
                                        <span className="ccg-name">{(row.designation?.label && typeof row.designation.label !== 'object') ? row.designation.label : '\u2014'}</span>
                                        <span className="ccg-company">{clientName}</span>
                                    </div>
                                </div>
                            }
                            body={
                                <div className="ccg-card-body-content">
                                    <div className="ccg-info-grid">
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="production.detail.project" defaultMessage="Projekti" /></span>
                                            <span className="ccg-info-value">{(row.project?.name && typeof row.project.name !== 'object') ? row.project.name : '\u2014'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="production.responsibilityArchive.card.department" defaultMessage="Reparti" /></span>
                                            <span className="ccg-info-value">{(row.department?.label && typeof row.department.label !== 'object') ? row.department.label : '\u2014'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="production.detail.quantity" defaultMessage="Sasia" /></span>
                                            <span className="ccg-info-value" style={{ fontWeight: 600 }}>{(row.unit !== null && row.unit !== undefined && typeof row.unit !== 'object') ? row.unit : '\u2014'}</span>
                                        </div>
                                        {row.price && (
                                            <div className="ccg-info-item">
                                                <span className="ccg-info-label"><FormattedMessage id="production.detail.value" defaultMessage="Vlera" /></span>
                                                <span className="ccg-info-value" style={{ color: '#1e40af', fontWeight: 600 }}>{Number(row.price).toFixed(2)}{'\u20AC'}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            }
                            footer={
                                <div className="ccg-card-footer">
                                    <span className="ccg-date">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                                        {(row.finishedProduct && typeof row.finishedProduct !== 'object') ? row.finishedProduct : (row.finishedProduct?.updatedAt || '\u2014')}
                                    </span>
                                </div>
                            }
                        />
                    );
                })}
            </div>
        );
    }

    return null;
};

export default WarehouseCardGrid;
